/**
 * BoardCanvas — the chrome-free canvas layer of the board.
 *
 * Owns the viewport hook, the recursive `SpaceRenderer`, the void
 * backdrop, the frame, and an embedded minimap (the minimap is
 * per-canvas because two panes in a side-by-side diff can frame
 * different regions). HUD chrome (breadcrumb, controls, depth/zoom
 * readout) lives in `BoardChrome` so it can be composed once across
 * multiple canvases.
 *
 * Surfaces viewport state via `onViewportChange` and exposes
 * imperative viewport controls through a forwarded ref so a parent
 * can mirror state across multiple canvas instances or render shared
 * chrome above them.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type MouseEvent as ReactMouseEvent } from "react";
import { Minimap } from "./Minimap";
import { DevSwitchesHud } from "./hud/DevSwitchesHud";
import {
  SpaceRenderer,
  type ItemDiagnostic,
  type ItemRenderContext,
} from "./SpaceRenderer";
import { type BoardItem, type BoardSpace, type SpaceLike } from "@archlang/scene/board";
import { createHoverStore, HoverStoreProvider, type HoverStore } from "./hoverStore";
import { createConnectionHoverStore, ConnectionHoverProvider, type ConnectionHoverStore } from "./connectionHoverStore.js";
import { createConnectionSelectionStore, ConnectionSelectionProvider, type ConnectionSelectionStore } from "./connectionSelectionStore.js";
import { useSpotlightHold } from "./useSpotlightHold";
import { useScopeDial, isMacPlatform } from "./useScopeDial";
import { useFrameNow } from "./useFrameNow";
import { useCameraHistory } from "./useCameraHistory";
import { ringTargets } from "./scopeRings";
import type { FrameTarget } from "@archlang/scene/board";
import { useViewport } from "./useViewport";
import { observeResize } from "./sharedResizeObserver";
import type { ViewportStore } from "./viewportStore";
import { asSource, type OperatorSignal, panFor, subspaceSlotRect, type Viewport, walkFocal } from "@archlang/scene/board";
import { nestedFloorRect } from "./nestedProjection";
import { computeContextLayer } from "./gravityWell";
import { viewBoundsFor } from "./viewBounds";
import { useCameraOperator, findContainingFocalPath } from "./cameraOperator";
import { useStepAutoFrame } from "./useStepAutoFrame";
import type { EdgeDecoMap } from "../process/edgeDecoration.js";
import {
  exportFocalAsPng,
  captureFocalPngBlob,
  imageClipboardSupported,
  writeImageToClipboard,
  type ExportFocalOptions,
} from "./exportPng";
import { exportFocalAsSvg } from "./exportSvg";
import { useLifecyclePaint } from "./lifecycle/useLifecycleState";
import { useBoardFocus } from "./focus/useBoardFocus.js";

export interface BoardCanvasProps {
  /** Either a fully-solved tree (diff / embed) or a lazy engine-backed source
   *  (the live board). Resolved through `asSource` downstream. */
  readonly root: SpaceLike;
  readonly selectedId?: string | null;
  readonly selectedEdgeId?: string | null;
  readonly activeProcessId?: string | null;
  readonly activeStepIndex?: number | null;
  readonly highlightedIds?: readonly string[];
  /** Node-only highlight: module ids that belong to a hovered/pinned
   *  legend aspect. Unlike `highlightedIds` (blast), this does NOT light
   *  the modules' edges. */
  readonly memberHighlightIds?: readonly string[];
  /** Edge highlight: qualified interface paths whose interface carries the
   *  aspect — the matching edges light up (a member's *explicit* surface,
   *  not its every connection). */
  readonly memberInterfacePaths?: readonly string[];
  readonly diagnosticsByItem?: ReadonlyMap<string, readonly ItemDiagnostic[]>;
  readonly debugLayout?: boolean;
  readonly onSelect?: (id: string | null) => void;
  readonly onSelectEdge?: (edgeId: string | null) => void;
  /** Click-reveal (VIEWS §7): a boundary stub was clicked — reveal its
   *  absent peer (qualified key) in the active view. */
  readonly onRevealBoundary?: (peerKey: string) => void;
  readonly onPlace?: (id: string, bounds: { x: number; y: number; w: number; h: number }) => void;
  readonly onUnpin?: (id: string) => void;
  readonly pinnedIds?: ReadonlySet<string>;
  /** When set, the node with this id renders with a ghost-repair highlight
   *  (dashed yellow outline, reduced opacity). Driven by the animation
   *  loop during the ghost / ghostSettle solver phases. */
  readonly ghostMoverId?: string | null;
  readonly initialViewport?: Viewport;
  readonly onViewportChange?: (vp: Viewport) => void;
  /** Resolve a module id to its containing space's focal path (model-derived).
   *  Lets flyTo reach modules below the lazy board's solved preview depth. */
  readonly moduleFocalPath?: (moduleId: string) => readonly string[] | null;
  /** Identity of the loaded workspace. When it changes (new import), the
   *  board discards prior pan/zoom and re-fits to the new content. */
  readonly resetKey?: unknown;
  /** Handle wheel for pan/zoom (default true). False = plain scroll
   *  bubbles to the page; ⌘/ctrl-zoom still works. For embeds. */
  readonly wheel?: boolean;
  /** Interactivity (default true). `false` = a live, projected, read-only
   *  preview: the wheel / drag / touch / keyboard hooks and the out-of-bounds
   *  rescue timer all stay inert (zero window/document listeners, no idle
   *  timer) — the posture for many-instance embeds. ANDs with `wheel` /
   *  `keyboardNav`. The camera/operator stack is already off in embeds
   *  (operator defaults false). Still the REAL board, projected over the shared
   *  model — an embed just composes a `BoardSurface interactive={false}` with no
   *  chrome, rather than flagging a full board. */
  readonly interactive?: boolean;
  /** Shared viewport store. When set, this canvas reads/writes the given
   *  store instead of owning its own — used by the side-by-side diff so
   *  both panes pan/zoom as one. The store owner seeds the initial fit. */
  readonly viewportStore?: ViewportStore;
  /** Bind global keyboard shortcuts (default true). Set false on all but
   *  one canvas when sharing a `viewportStore`, so a keypress isn't applied
   *  once per canvas to the same store. */
  readonly keyboardNav?: boolean;
  /** Hide the embedded minimap. The chrome readout/breadcrumb sit in
   *  the sibling `BoardChrome`; the minimap stays here because it
   *  belongs to its specific canvas. */
  readonly hideMinimap?: boolean;
  /** DEV-only extra chips docked above the minimap (layout debug controls).
   *  Stacked over the layout-switch toggles; rendered only when passed. */
  readonly minimapExtras?: ReactNode;
  /** Phone layout: the minimap moves to the top-right (clear of the mobile
   *  board header above it and of thumbs at the screen bottom) and shrinks
   *  to 80%. */
  readonly mobile?: boolean;
  /** Per-edge decoration map produced by the process walker. When set,
   *  edges acquire step-number chips, modifier badges, and the
   *  scrubber pulse. */
  readonly edgeDecoMap?: EdgeDecoMap;
  /** Node performing the current SELF-REFERENTIAL step (self-call / note /
   *  await) — ringed on the board, since no edge carries the highlight. */
  readonly stepActorId?: string | null;
  /** The current step's two endpoint node ids (equal for self rows). Drives
   *  auto-framing: a step outside the viewport moves the camera to show both,
   *  remembering (and later restoring) the viewport it left. */
  readonly stepParticipants?: { readonly aId: string; readonly bId: string } | null;
  /** Camera operator ("smart view") — when enabled, the viewport gently
   *  follows edits/selection. Off unless a host wires the signals below. */
  readonly operatorEnabled?: boolean;
  /** Latest edit signal for the camera operator (new identity per emission). */
  readonly operatorEditSignal?: OperatorSignal | null;
  /** Latest selection signal for the camera operator. */
  readonly operatorSelectionSignal?: OperatorSignal | null;
  /** Emitted when the editor selection clears — triggers the peek drift-back.
   *  `graceful`: caret stayed inside the framed node (delay the return so
   *  typing can take over) vs moved away (return immediately). */
  readonly operatorSelectionCleared?: { readonly nonce: number; readonly graceful: boolean } | null;
  /** Board ids the editor caret currently sits inside (innermost first). Holds a
   *  framed node while the caret is parked in it; empty when the caret is outside
   *  any declaration. */
  readonly operatorCaretIds?: readonly string[];
  /** The operator surface is INTERACTIVE (a real toggle exists — not the forced
   *  demo, not a read-only embed). Gates the spotlight-hold / scope-dial hotkeys
   *  so a page of forced demo boards doesn't bind them all. */
  readonly operatorInteractive?: boolean;
  /** 1-hop neighbours of a board id (edge-derived, board-key domain) — powers
   *  the scope dial's ring 2. Supplied by the host from the dependency edges. */
  readonly operatorNeighborsOf?: (id: string) => readonly string[];
  /** Shared hover store. When set, the canvas reads/writes the given store
   *  instead of owning its own — lets the nav pane mirror board hover (and
   *  vice versa). */
  readonly hoverStore?: HoverStore;
  /** Fires on the double-click "bring into view" action (dive into a space
   *  or zoom-frame a leaf) with the item id — hosts use it to scroll the
   *  nav pane to the matching row. */
  readonly onItemOpen?: (id: string) => void;
  /** Cmd/Ctrl-click an item → open it in a new focused board tab. `isSpace`
   *  flags items with nested contents so the host can root INTO the space
   *  rather than just frame the node. */
  readonly onItemOpenRooted?: (id: string, isSpace?: boolean) => void;
  /** Right-click an item → open a context menu at the pointer (viewport coords). */
  readonly onItemContextMenu?: (id: string, x: number, y: number) => void;
  /** Right-click the empty board (not on any item) → open a board-level menu.
   *  Items stop propagation, so this only fires on the void backdrop. */
  readonly onBackgroundContextMenu?: (e: ReactMouseEvent) => void;
  /** Right-click a connection/link or an interface port → open its menu. */
  readonly onEdgeContextMenu?: (edgeId: string, x: number, y: number) => void;
  /** Click a diagnostic in an item's issue popover → open it in the editor at
   *  its exact source location. */
  readonly onOpenDiagnostic?: (id: string, diag: ItemDiagnostic) => void;
}

export interface BoardCanvasHandle {
  /** Navigate the viewport to focus on a module anywhere in the tree. */
  flyTo(moduleId: string): void;
  /** Navigate INTO a space by id (focal becomes the space — its contents
   *  fill the board). The rooted-tab counterpart of `flyTo`. */
  enterSpace(moduleId: string): void;
  /** Snapshot the current viewport. */
  getViewport(): Viewport;
  /** Animated viewport restore (matches sidebar back-nav semantics). */
  restoreViewport(vp: Viewport): void;
  /** Non-animated viewport replacement — used by the diff mirror. */
  setViewportImmediate(vp: Viewport): void;
  /** Pop one focal level (esc). Exposed for shared chrome. */
  popOut(): void;
  /** Reset to the root focal + fit-to-screen. */
  resetView(): void;
  /** Re-fit the current focal to the container's current size (host-driven
   *  reshape, e.g. switching the editor/board split orientation). Keeps the
   *  focal path, unlike `resetView`. */
  reframe(): void;
  /** Navigate to an arbitrary focal path. */
  navigateTo(path: readonly string[]): void;
  /** Frame a connection's two endpoint nodes together — see
   *  `ViewportControls.framePair` for the ancestor rules. */
  framePair(aId: string, bId: string): void;
  /** Export the current focal space as a PNG download. */
  exportFocalAsPng(opts?: ExportFocalOptions): Promise<void>;
  /** Export the current focal space as an SVG download. */
  exportFocalAsSvg(opts?: ExportFocalOptions): Promise<void>;
  /** Copy the current focal space to the clipboard as a PNG. */
  copyFocalToClipboard(opts?: ExportFocalOptions): Promise<void>;
}

// KNOWN ISSUE (Safari) — full investigation: docs/SAFARI-BOARD-RENDERING.md.
// At deep zoom WebKit rasterizes the board's
// composited layer at a clamped scale, leaving it mildly soft at idle
// (~2.4× lower edge sharpness than Chromium at 5×, measured). Chromium
// re-rasterizes crisp on the idle demote below. An extensive live-Safari
// bisection (2026-06-11) ruled out: transform churn, will-change, 3D vs
// 2D transforms, clip-path/overflow extent bounding, animations,
// backdrop-filter, and the board's own DOM. The trigger correlates with
// the LEFT NAV PANEL's content subtree being present on the page
// (board-only page renders crisp); the exact element wasn't isolated.
// CSS `zoom` and stage-windowing alternatives rendered crisp but caused
// visible layout shifts / widget quirks and were rejected. Accepting the
// mild idle softness for now — rendering is at least consistent between
// moving and idle.

// Stable empty caret-id list so the spotlight hook's identity check doesn't
// churn when no caret ids are supplied.
const EMPTY_CARET_IDS: readonly string[] = [];
// Stable no-neighbours resolver (dial ring 2 collapses to ring 1 without edges).
const EMPTY_NEIGHBORS = (): readonly string[] => [];
// Shared empty default for optional id-list props. A literal `= []` default
// in the destructure mints a FRESH array on every render when the prop is
// absent — which re-fires the `new Set(...)` memos below, which changes
// `ctx` identity, which breaks EVERY ItemRenderer/EdgePath memo: the whole
// board tree re-rendered on every gesture frame (measured: 1100 components
// per commit on a 324-module board; the entire "quant-step cliff" tail).
const EMPTY_IDS: readonly string[] = [];

export const BoardCanvas = forwardRef<BoardCanvasHandle, BoardCanvasProps>(function BoardCanvas(
  {
    root,
    selectedId = null,
    selectedEdgeId = null,
    activeProcessId = null,
    activeStepIndex = null,
    highlightedIds = EMPTY_IDS,
    memberHighlightIds = EMPTY_IDS,
    memberInterfacePaths = EMPTY_IDS,
    diagnosticsByItem,
    debugLayout = false,
    onSelect,
    onSelectEdge,
    onRevealBoundary,
    onPlace,
    onUnpin,
    pinnedIds,
    ghostMoverId = null,
    initialViewport,
    onViewportChange,
    moduleFocalPath,
    resetKey,
    hideMinimap = false,
    minimapExtras,
    mobile = false,
    edgeDecoMap,
    stepActorId = null,
    stepParticipants = null,
    wheel = true,
    interactive = true,
    viewportStore,
    keyboardNav = true,
    operatorEnabled = false,
    operatorEditSignal = null,
    operatorSelectionSignal = null,
    operatorSelectionCleared = null,
    operatorCaretIds,
    operatorInteractive = false,
    operatorNeighborsOf,
    hoverStore: externalHoverStore,
    onItemOpen,
    onItemOpenRooted,
    onOpenDiagnostic,
    onItemContextMenu,
    onBackgroundContextMenu,
    onEdgeContextMenu,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Stable hook the gesture listeners call on real manual input; points at the
  // camera operator's `noteUserNavigation` (wired just below) so a hand on the
  // board pauses auto-motion. A ref keeps `onUserGesture`'s identity stable so
  // the gesture effects don't re-bind every render.
  const noteUserNavRef = useRef<() => void>(() => {});
  // A real board gesture also latches any active spotlight hold dead until keyup
  // (wired below, once the hold hook exists).
  const spotlightGestureRef = useRef<() => void>(() => {});
  const onUserGesture = useCallback(() => {
    noteUserNavRef.current();
    spotlightGestureRef.current();
  }, []);
  // Touch double-tap → open the item, same action as desktop double-click.
  // Stable wrapper over a ref for the same listener-rebinding reason as
  // `onUserGesture`; the resolver itself is assigned below once `ctx` exists.
  const openItemRef = useRef<(id: string) => boolean>(() => false);
  const onItemDoubleTap = useCallback((id: string) => openItemRef.current(id), []);
  // captureMode toggles a render path that disables viewport-bounds
  // culling and forces scale=1 (full LOD) so the PNG export captures
  // the entire focal space at native detail regardless of where the
  // user is panned/zoomed.
  const [captureMode, setCaptureMode] = useState(false);
  const {
    store,
    viewport,
    focal,
    diveInto,
    frameOn,
    popOut,
    navigateTo,
    resetView,
    refit,
    flyTo,
    enterSpace,
    restoreViewport,
    setViewportImmediate,
    centerOn,
    framePair,
    screenSize,
    animateTo,
  } = useViewport(root, containerRef, {
    initialViewport,
    wheel,
    interactive,
    store: viewportStore,
    keyboard: keyboardNav,
    moduleFocalPath,
    resetKey,
    onUserGesture,
    onItemDoubleTap,
  });

  const liveViewportRef = useRef(viewport);
  liveViewportRef.current = viewport;

  // The lifecycle paint the board is showing right now, so a vector export
  // matches the screen it was taken from. Held in a ref for the same reason the
  // viewport is: the export command is built once and would otherwise close
  // over whatever the paint was at mount. Null when no provider is above us.
  const lifecyclePaint = useLifecyclePaint();
  const lifecyclePaintRef = useRef(lifecyclePaint);
  lifecyclePaintRef.current = lifecyclePaint;
  // The active view's figure/ground split, for the vector export: a file taken
  // off a focused board has to carry the focus or it contradicts the screen it
  // came from — the same argument that put lifecycle paint in the export.
  const boardFocus = useBoardFocus();
  const boardFocusRef = useRef(boardFocus);
  boardFocusRef.current = boardFocus;

  // Camera operator ("smart view"): gently follows edits/selection. Pure policy
  // lives in @archlang/scene/board; this binds it to the live viewport. Inert
  // unless `operatorEnabled` and a host wires the signals. Its manual-override
  // hook is fed back into the gesture listeners via `noteUserNavRef`.
  const getViewport = useCallback(() => liveViewportRef.current, []);
  const operatorFocalPathOf = useCallback(
    (id: string): readonly string[] | null =>
      moduleFocalPath?.(id) ?? findContainingFocalPath(root, id),
    [moduleFocalPath, root],
  );
  // Scope-dial altitude (session-scoped; resets on a workspace swap, never
  // persisted). Drives both the explicit dial press and the follow-at-altitude
  // expansion the operator applies to later edits.
  const [ring, setRing] = useState(1);
  useEffect(() => setRing(1), [resetKey]);
  const neighborsOf = operatorNeighborsOf ?? EMPTY_NEIGHBORS;
  const expandTarget = useCallback(
    (ids: readonly string[]): FrameTarget => {
      // Only a single-node edit expands by altitude; region/multi edits frame as
      // themselves. Ring 1 is the identity.
      if (ids.length !== 1 || ring === 1) return { ids };
      return (
        ringTargets(ring, ids[0]!, { neighbors: neighborsOf, focalPathOf: operatorFocalPathOf }) ?? {
          ids,
        }
      );
    },
    [ring, neighborsOf, operatorFocalPathOf],
  );
  const operator = useCameraOperator({
    enabled: operatorEnabled,
    editSignal: operatorEditSignal,
    selectionSignal: operatorSelectionSignal,
    selectionCleared: operatorSelectionCleared,
    caretIds: operatorCaretIds,
    expandTarget,
    source: root,
    getViewport,
    screenSize,
    focalPathOf: operatorFocalPathOf,
    animateTo,
    setViewportImmediate,
    resetKey,
  });
  // Step auto-framing: an out-of-view step frames its participants and
  // remembers where the camera was; a later step that fits the remembered
  // view (or closing the scrubber) returns home. Manual gestures adopt the
  // user's view as the new home (wired into onUserGesture below).
  const autoFrame = useStepAutoFrame(stepParticipants, {
    source: root,
    getViewport,
    screenSize,
    focalPathOf: operatorFocalPathOf,
    framePair,
    restoreViewport,
  });
  noteUserNavRef.current = () => {
    operator.noteUserNavigation();
    autoFrame.noteUserGesture();
  };

  // For export/capture we need the WHOLE focal subtree in the DOM, not just the
  // lazy preview depth — resolve the focal at full depth while capturing. The
  // focal frame is depth-independent, so only the rendered + exported space
  // swaps; width/height/minimap keep using `focal`.
  const renderFocal = useMemo(
    () => (captureMode ? asSource(root).at(viewport.focalPath, Infinity) : focal),
    [captureMode, root, viewport.focalPath, focal],
  );

  // A DIVED focal keeps the well it had as a preview: its floor + grid fill
  // the container's whole subspace slot (`nestedFloorRect` of the same solver
  // slot rect the preview used), instead of snapping back to shrink-wrapping
  // the space frame at the dive instant. Root focal (not dived) → undefined.
  const focalFloorRect = useMemo(() => {
    if (viewport.focalPath.length <= 1) return undefined;
    const steps = walkFocal(asSource(root), viewport.focalPath);
    const container = steps[steps.length - 1]?.containerItem;
    if (!container) return undefined;
    const slot = subspaceSlotRect(container);
    return nestedFloorRect(focal, slot.w, slot.h);
  }, [root, viewport.focalPath, focal]);

  // Run an export against the content div with capture mode engaged.
  // captureMode makes SpaceRenderer render at scale=1 (no LOD culling,
  // all items + nested spaces present) and ignore viewport-bounds
  // culling; the live transform stays and the export helper overrides it
  // via modern-screenshot's style option. Two rAFs: the first lets React
  // commit the captureMode flip, the second lets the browser paint the
  // re-rendered DOM before modern-screenshot snapshots styles.
  const withCapture = useCallback(
    async <T,>(fn: (el: HTMLElement) => Promise<T>): Promise<T> => {
      const el = contentRef.current;
      if (!el) throw new Error("board content not mounted");
      setCaptureMode(true);
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      try {
        return await fn(el);
      } finally {
        setCaptureMode(false);
      }
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      flyTo,
      enterSpace,
      getViewport: () => liveViewportRef.current,
      restoreViewport,
      setViewportImmediate,
      popOut,
      resetView,
      reframe: refit,
      navigateTo,
      framePair,
      exportFocalAsPng: (opts) =>
        withCapture((el) => exportFocalAsPng(el, asSource(root).at(liveViewportRef.current.focalPath, Infinity), opts)),
      // Vector export renders headless from the placed model — no DOM
      // capture, no captureMode round-trip.
      exportFocalAsSvg: (opts) =>
        exportFocalAsSvg(asSource(root).at(liveViewportRef.current.focalPath, Infinity), {
          ...opts,
          ...(lifecyclePaintRef.current ? { lifecycle: lifecyclePaintRef.current } : {}),
          ...(boardFocusRef.current ? { focus: boardFocusRef.current.figure } : {}),
        }),
      copyFocalToClipboard: (opts) => {
        if (!imageClipboardSupported()) {
          return Promise.reject(new Error("Copying images isn't supported in this browser"));
        }
        // Safari: the clipboard write must START synchronously in the user
        // gesture — the capture runs inside the Promise<Blob> the
        // ClipboardItem carries, while write() is already in flight.
        return writeImageToClipboard(
          withCapture((el) =>
            captureFocalPngBlob(el, asSource(root).at(liveViewportRef.current.focalPath, Infinity), opts),
          ),
        );
      },
    }),
    [flyTo, enterSpace, restoreViewport, setViewportImmediate, popOut, resetView, refit, navigateTo, framePair, root, withCapture],
  );

  // Notify the host of viewport changes. The callback is read through a
  // ref so the effect depends only on `viewport` — callers commonly pass
  // an inline arrow (new identity every render), and listing it in the
  // deps would re-fire this effect on every parent render. During a node
  // drag the parent re-renders each frame, so that turned the setState in
  // here into an "Maximum update depth exceeded" feedback loop.
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  useEffect(() => {
    onViewportChangeRef.current?.(viewport);
  }, [viewport]);

  // Transient layer promotion. The content layer is GPU-composited via
  // `will-change: transform` so pan/zoom stay smooth. But a promoted layer
  // caches ONE raster at the scale it was last drawn, then GPU-magnifies
  // that texture as `scale()` grows — so zooming in from afar shows a
  // blurry, stretched bitmap until a reload forces a fresh raster. Fix:
  // promote only while the viewport is actively moving, and demote ~200ms
  // after it settles — in Chromium dropping `will-change` to `auto` (with
  // the 2D translate form) makes the browser re-rasterize crisp at the
  // live zoom.
  //
  // Imperative on a store subscription, NOT React state: a per-frame
  // `setVpActive(true)` from a passive effect measurably doubled the
  // commit count during gestures (one commit for the store's sync render,
  // one for the state write — 2.0 → 1.0 commits/frame with this removed).
  // React never writes `will-change` here (absent from the style prop), so
  // the direct mutation and the per-frame render can't fight.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let promoted = false;
    const unsub = store.subscribe(() => {
      const el = contentRef.current;
      if (!el) return;
      if (!promoted) {
        promoted = true;
        el.style.willChange = "transform";
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        promoted = false;
        const idle = contentRef.current;
        if (idle) idle.style.willChange = "auto";
      }, 200);
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [store]);

  // Steps left here as a local memo even though chrome doesn't render
  // them: nested-space DOM walking inside `SpaceRenderer` benefits from
  // the same `walkFocal` call being cheap to recompute.
  void useMemo(() => walkFocal(root, viewport.focalPath), [root, viewport.focalPath]);

  const highlighted = useMemo(() => new Set(highlightedIds), [highlightedIds]);
  const memberHighlighted = useMemo(() => new Set(memberHighlightIds), [memberHighlightIds]);
  const memberInterfaces = useMemo(() => new Set(memberInterfacePaths), [memberInterfacePaths]);
  const emptyDiag = useMemo(() => new Map<string, readonly ItemDiagnostic[]>(), []);
  const emptyPinned = useMemo(() => new Set<string>(), []);
  // Global hover state. A single pointermove on the outer container
  // does the hit-test (event.target.closest('[data-board-item]'))
  // and writes the innermost item id into a tiny external store. This
  // sidesteps the GPU-compositing :hover staleness bug in Chrome by
  // using pointermove (which fires every sample while the cursor is
  // moving) as the source of truth, and uses closest() so nested items
  // mask their parents naturally. The store (not `ctx`) carries hover so
  // a hover change re-renders only the two affected items — see
  // hoverStore.ts.
  const internalHoverStore = useRef<HoverStore | null>(null);
  if (internalHoverStore.current === null) internalHoverStore.current = createHoverStore();
  const hoverStore = externalHoverStore ?? internalHoverStore.current;

  // Connection-hover store — shared across ALL spaces (context + focal + previews)
  // so hovering one segment lights the whole connection across every level.
  const connHoverRef = useRef<ConnectionHoverStore | null>(null);
  if (connHoverRef.current === null) connHoverRef.current = createConnectionHoverStore();
  const connHoverStore = connHoverRef.current;

  // Connection-SELECTION store — persistent; click any piece → the whole
  // connection is selected across every level.
  const connSelRef = useRef<ConnectionSelectionStore | null>(null);
  if (connSelRef.current === null) connSelRef.current = createConnectionSelectionStore();
  const connSelStore = connSelRef.current;

  // Anchor for the explicit verbs: the caret's node, else the board selection.
  const verbAnchorId = useCallback(
    (): string | null =>
      (operatorCaretIds && operatorCaretIds.length > 0 ? operatorCaretIds[0] : selectedId) ?? null,
    [operatorCaretIds, selectedId],
  );

  // The explicit "show me" verbs (spotlight, overview-peek, dial, frame-now,
  // camera-history) bind on any INTERACTIVE, non-forced surface — they work
  // whether or not smart-view autopilot is toggled on (Model A: the toggle gates
  // AUTO motion only). `operatorInteractive` is already `operator && !forced`.

  // Spotlight hold (⌥): live-track the caret / pointer while held, glide back on
  // release. Add Shift (⌥⇧) to widen to the overview of the anchor's space. A
  // board gesture latches an active hold dead via onGesture.
  const spotlight = useSpotlightHold({
    active: operatorInteractive,
    caretIds: operatorCaretIds ?? EMPTY_CARET_IDS,
    hoverStore,
    start: operator.spotlightStart,
    target: operator.spotlightTarget,
    overview: operator.spotlightOverview,
    end: operator.spotlightEnd,
  });
  spotlightGestureRef.current = spotlight.onGesture;

  // Scope dial (⌃⌥↑/↓, macOS only): step the framing radius through semantic
  // rings around the anchor.
  useScopeDial({
    active: operatorInteractive && isMacPlatform(),
    anchorId: verbAnchorId,
    neighbors: neighborsOf,
    focalPathOf: operatorFocalPathOf,
    frameAtRing: operator.frameAtRing,
    ring,
    setRing,
  });

  // Frame-now (⌃⌥⏎): frame the anchor at the current ring, right now.
  useFrameNow({
    active: operatorInteractive,
    anchorId: verbAnchorId,
    neighbors: neighborsOf,
    focalPathOf: operatorFocalPathOf,
    ring,
    frameAtRing: operator.frameAtRing,
  });

  // Camera history (⌃⌥←/→): step back/forward through camera rest positions.
  useCameraHistory({
    active: operatorInteractive,
    store,
    animateTo,
    setViewportImmediate,
    onStep: onUserGesture,
    resetKey,
  });
  // Live/quantized zoom ratio for drags — items render with the snapshot
  // quantZoom, but a drag must convert pointer px at the LIVE zoom or the
  // node drifts from the pointer (up to ~25% at low zoom). Stable callback
  // reading through a ref (stamped below, next to the quantZoom derivation)
  // so it never churns the ctx memo.
  const quantZoomRef = useRef(1);
  const dragZoomCorrection = useCallback(() => {
    const q = quantZoomRef.current;
    return q > 0 ? store.get().zoom / q : 1;
  }, [store]);

  const ctx: ItemRenderContext = useMemo(
    () => ({
      selectedId,
      dragZoomCorrection,
      selectedEdgeId,
      activeProcessId,
      activeStepIndex,
      highlighted,
      memberHighlighted,
      memberInterfaces,
      diagnosticsByItem: diagnosticsByItem ?? emptyDiag,
      debugLayout,
      pinnedIds: pinnedIds ?? emptyPinned,
      ghostMoverId: ghostMoverId ?? null,
      edgeDecoMap,
      stepActorId,
      onSelect: (id) => onSelect?.(id === selectedId ? null : id),
      onSelectEdge: (edgeId) => onSelectEdge?.(edgeId === selectedEdgeId ? null : edgeId),
      // Only when actually wired — a fabricated no-op wrapper would defeat
      // SpaceRenderer's fallback (boundary pill → connection-selection) for
      // consumers that render stubs without a reveal handler.
      ...(onRevealBoundary ? { onRevealBoundary } : {}),
      // Double-click "bring into view": dive into a nested space, or zoom-
      // frame a leaf module (universal — a leaf dbl-click is never a no-op).
      onDive: (item: BoardItem) => {
        if (item.space) diveInto(item);
        else frameOn(item);
        onItemOpen?.(item.id);
      },
      onOpenRooted: onItemOpenRooted
        ? (item: BoardItem) => onItemOpenRooted(item.id, Boolean(item.space))
        : undefined,
      onOpenDiagnostic: onOpenDiagnostic
        ? (item: BoardItem, diag: ItemDiagnostic) => onOpenDiagnostic(item.id, diag)
        : undefined,
      onItemContextMenu: onItemContextMenu
        ? (item: BoardItem, x: number, y: number) => onItemContextMenu(item.id, x, y)
        : undefined,
      onEdgeContextMenu,
      onPlace: (id, bounds) => onPlace?.(id, bounds),
      onUnpin: (id) => onUnpin?.(id),
    }),
    [
      dragZoomCorrection,
      selectedId,
      selectedEdgeId,
      activeProcessId,
      activeStepIndex,
      highlighted,
      memberHighlighted,
      memberInterfaces,
      diagnosticsByItem,
      debugLayout,
      pinnedIds,
      ghostMoverId,
      emptyDiag,
      emptyPinned,
      edgeDecoMap,
      stepActorId,
      onSelect,
      onSelectEdge,
      onRevealBoundary,
      diveInto,
      frameOn,
      onItemOpen,
      onItemOpenRooted,
      onOpenDiagnostic,
      onItemContextMenu,
      onEdgeContextMenu,
      onPlace,
      onUnpin,
    ],
  );

  // Resolve a touch double-tap's item id against the current focal tree and
  // route it through the same onDive path as desktop double-click (dive a
  // space / zoom-frame a leaf + notify the nav pane).
  openItemRef.current = (id: string): boolean => {
    const find = (s: BoardSpace): BoardItem | null => {
      for (const it of s.items) {
        if (it.id === id) return it;
        if (it.space) {
          const hit = find(it.space);
          if (hit) return hit;
        }
      }
      return null;
    };
    const item = find(focal);
    if (!item) return false;
    ctx.onDive(item);
    return true;
  };

  const handleHoverMove = useCallback((e: React.PointerEvent) => {
    // Touch leaves no hover: a panning finger painting hover highlights under
    // itself reads as flicker, and nothing un-hovers after the lift.
    if (e.pointerType === "touch") return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const wrapper = target.closest("[data-board-item]") as HTMLElement | null;
    // A `replicated` copy hovers by its logical key, so every copy glows.
    hoverStore.setHoveredId(wrapper?.dataset.logicalId ?? wrapper?.dataset.boardItem ?? null);
  }, [hoverStore]);
  const handleHoverLeave = useCallback(() => {
    hoverStore.setHoveredId(null);
  }, [hoverStore]);

  // Quantize the inputs that drive SpaceRenderer re-renders. The wrapper
  // <div>'s CSS transform handles visual pan/zoom every frame for free —
  // SpaceRenderer only needs fresh props when crossing a culling or
  // detail-fade threshold. Without quantization, every pinch/wheel event
  // recomputes viewBounds + zoom and forces a recursive re-render of the
  // whole space tree, which is the dominant cost during gestures.
  const ZOOM_STEP = 0.05;
  const BOUNDS_STEP = 200;
  // Live canvas size, ResizeObserver-driven. The mobile tab layout HIDES the
  // canvas (display:none → 0×0) instead of unmounting it; a viewBounds
  // computed while hidden culls EVERYTHING and — since its memo deps are
  // quantized pan/zoom only — stays stale after the canvas reappears (blank
  // board until a big pan). Tracking the size as state makes reappearance a
  // dep change, and a 0-size canvas skips culling entirely.
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  // Measure synchronously before paint: the render transform now DERIVES pan
  // from `canvasSize` (pan = size/2 - center*zoom), so a stale 0×0 size on the
  // first paint would flash the content at the top-left for one frame. A layout
  // effect lands the real size before the browser paints.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCanvasSize((prev) =>
      prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height },
    );
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    return observeResize(el, () => {
      const r = el.getBoundingClientRect();
      setCanvasSize((prev) =>
        prev.width === r.width && prev.height === r.height
          ? prev
          : { width: r.width, height: r.height },
      );
    });
  }, []);
  // Derive the screen-pixel pan from the centre-canonical viewport + the live
  // canvas size. This is the single bridge from the size-independent stored
  // viewport to the CSS transform / culling / minimap.
  const pan = panFor(viewport, canvasSize.width, canvasSize.height);

  // ── Gesture-gated quantization snapshot ─────────────────────────────────
  // Everything the memoized SpaceRenderer tree derives its props from
  // (quantized zoom for LOD, quantized pan for culling) reads a SNAPSHOT of
  // the viewport, not the live per-frame value. At real gesture velocity the
  // 0.05-zoom step is crossed nearly every frame (a pinch moves zoom
  // 2–8%/frame) and at low zoom a pan crosses a 200px cell every few frames —
  // deriving quant values per render turned the "rare" full-tree re-render
  // into a per-frame one (the quant-step cliff; see the 2026-07-07
  // board-gesture-performance plan). The snapshot refreshes at most every
  // QUANT_REFRESH_MS while a gesture drives the store, immediately on a
  // focal-path change (a lagging cull frame in the WRONG coordinate space
  // would cull everything for a tick), and once more when the gesture
  // settles. The setState runs synchronously inside the store notification,
  // so a refresh batches into the same commit as the store's own render —
  // commits/frame stays 1.
  const QUANT_REFRESH_MS = 400;
  const QUANT_SETTLE_MS = 180;
  const [quantView, setQuantView] = useState<{ vp: Viewport; pad: number }>(() => ({
    vp: store.get(),
    pad: 0,
  }));
  const canvasSizeRef = useRef(canvasSize);
  canvasSizeRef.current = canvasSize;
  useEffect(() => {
    let lastTick = 0;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    let prev = store.get();
    let prevT = performance.now();
    const unsub = store.subscribe(() => {
      const vp = store.get();
      const now = performance.now();
      const { width, height } = canvasSizeRef.current;
      // Camera velocity in screen px/ms: pan motion + the edge sweep a zoom
      // change causes. Sized into an overscan pad so the cull set stays
      // ahead of the camera between snapshot ticks. Quantized to coarse
      // steps so a steady gesture doesn't churn the pad value (a changed
      // pad is a changed SpaceRenderer prop).
      const focalChanged = vp.focalPath !== prev.focalPath;
      let pad = 0;
      if (!focalChanged && width > 0) {
        const pPrev = panFor(prev, width, height);
        const pNow = panFor(vp, width, height);
        const dPan = Math.hypot(pNow.x - pPrev.x, pNow.y - pPrev.y);
        const dZoomEdge =
          prev.zoom > 0 ? (Math.abs(vp.zoom - prev.zoom) / prev.zoom) * (Math.hypot(width, height) / 2) : 0;
        const dt = Math.max(1, now - prevT);
        const vel = (dPan + dZoomEdge) / dt;
        pad = Math.min(2400, Math.ceil((vel * QUANT_REFRESH_MS * 2) / 300) * 300);
      }
      prev = vp;
      prevT = now;
      if (focalChanged || now - lastTick >= QUANT_REFRESH_MS) {
        lastTick = now;
        setQuantView({ vp, pad });
      }
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        setQuantView({ vp: store.get(), pad: 0 });
      }, QUANT_SETTLE_MS);
    });
    return () => {
      unsub();
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [store]);

  const quantZoom = Math.max(ZOOM_STEP, Math.round(quantView.vp.zoom / ZOOM_STEP) * ZOOM_STEP);
  quantZoomRef.current = quantZoom;
  // Quantize pan to BOUNDS_STEP cells OUTSIDE the memo and depend on the
  // quantized primitives — not the snapshot object. The snapshot changes
  // identity every refresh tick; the quantized values only change when a
  // cell is actually crossed, so within a cell the viewBounds object stays
  // referentially stable and the memoized SpaceRenderer doesn't re-render.
  const quantPan = panFor(quantView.vp, canvasSize.width, canvasSize.height);
  const quantPanX = Math.round(quantPan.x / BOUNDS_STEP) * BOUNDS_STEP;
  const quantPanY = Math.round(quantPan.y / BOUNDS_STEP) * BOUNDS_STEP;
  const overscanPad = quantView.pad;
  const viewBounds = useMemo(
    () =>
      viewBoundsFor(quantPanX, quantPanY, quantZoom, canvasSize.width, canvasSize.height, ZOOM_STEP, BOUNDS_STEP, overscanPad),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quantZoom, quantPanX, quantPanY, overscanPad, canvasSize],
  );

  // ── Gravity-well context layer ──────────────────────────────────────────
  // When enabled and diven at least one level, render the focal's PARENT
  // behind the focal so the outer world stays visible. Pure projection: the
  // parent-frame viewport is `popFocal(viewport, container)` — the exact
  // inverse math that makes dive/pop seamless — so the two layers align by
  // construction at the dive instant (a fixed-pixel-header seam opens away
  // from it, masked by the depth-of-field fade below). The focal container is
  // drawn frame-only (`holeChildId`); the focal layer fills its interior.
  const contextLayer = useMemo(
    () => computeContextLayer(root, viewport, canvasSize, !captureMode),
    [captureMode, root, viewport, canvasSize],
  );
  // The context layer's TRANSFORM tracks the live camera (computed above);
  // its quantized cull/LOD inputs read the gesture-gated snapshot like the
  // focal layer, so a dived gesture doesn't re-render the parent tree on
  // every quant crossing either.
  const quantContextLayer = useMemo(
    () => computeContextLayer(root, quantView.vp, canvasSizeRef.current, !captureMode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captureMode, root, quantView, canvasSize],
  );
  const contextQuantZoom = quantContextLayer
    ? Math.max(ZOOM_STEP, Math.round(quantContextLayer.zoom / ZOOM_STEP) * ZOOM_STEP)
    : 0;
  // Cull the context layer the same way the focal is culled: only the on-screen
  // surround costs anything, and far parent siblings (+ their nested previews)
  // drop out. Without this the context renders the WHOLE parent tree every
  // frame — cheap on the demo, but O(parent size) on a real model. Quantize the
  // context pan to BOUNDS_STEP cells so the memo (and the memo'd context
  // SpaceRenderer) stays referentially stable while panning within a cell.
  const ctxQuantPanX = quantContextLayer ? Math.round(quantContextLayer.pan.x / BOUNDS_STEP) * BOUNDS_STEP : 0;
  const ctxQuantPanY = quantContextLayer ? Math.round(quantContextLayer.pan.y / BOUNDS_STEP) * BOUNDS_STEP : 0;
  const hasContext = !!contextLayer && !!quantContextLayer;
  const contextViewBounds = useMemo(() => {
    if (!hasContext) return undefined;
    return viewBoundsFor(ctxQuantPanX, ctxQuantPanY, contextQuantZoom, canvasSize.width, canvasSize.height, ZOOM_STEP, BOUNDS_STEP, overscanPad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContext, contextQuantZoom, ctxQuantPanX, ctxQuantPanY, overscanPad, canvasSize]);

  const handlePaneClick = () => {
    onSelect?.(null);
    onSelectEdge?.(null);
    connSelStore.setSelected(null); // click empty canvas → deselect the connection
  };

  return (
    <div
      ref={containerRef}
      data-board-canvas
      className="relative w-full h-full overflow-hidden overscroll-none bg-slate-950 select-none"
      style={{ cursor: "grab", touchAction: "none", WebkitTouchCallout: "none" }}
      onClick={handlePaneClick}
      onContextMenu={onBackgroundContextMenu}
      onPointerMove={handleHoverMove}
      onPointerLeave={handleHoverLeave}
    >
      <VoidBackdrop />
      <ConnectionSelectionProvider value={connSelStore}>
      <ConnectionHoverProvider value={connHoverStore}>
      {contextLayer && (
        <div
          data-board-context
          className="absolute top-0 left-0 origin-top-left"
          style={{
            width: Math.max(1, contextLayer.parentSpace.originX + contextLayer.parentSpace.width),
            height: Math.max(1, contextLayer.parentSpace.originY + contextLayer.parentSpace.height),
            transform: `translate(${contextLayer.pan.x}px, ${contextLayer.pan.y}px) scale(${contextLayer.zoom})`,
            transformOrigin: "0 0",
            // Non-interactive surround — the focal layer on top owns all input.
            // No depth-of-field fade: the two layers align by construction (both
            // derive from one viewport), so there is no seam to mask, and moving
            // off the focal reanchors (pops) into the parent rather than leaving
            // a blurred backdrop. The focal frame outline marks the active region.
            pointerEvents: "none",
          }}
        >
          <SpaceRenderer
            space={contextLayer.parentSpace}
            scale={contextQuantZoom}
            viewBounds={contextViewBounds}
            depth={0}
            ctx={ctx}
            context
            holeChildId={contextLayer.holeId}
          />
        </div>
      )}
      <div
        ref={contentRef}
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: Math.max(1, focal.originX + focal.width),
          height: Math.max(1, focal.originY + focal.height),
          // Always the 2D form; promotion during motion comes from the
          // imperative `will-change` toggle above (which alone pins a
          // layer), and the 2D form is what lets the demoted idle layer
          // re-rasterize crisp.
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${viewport.zoom})`,
          transformOrigin: "0 0",
        }}
      >
        {/* The space "floor" (dark rounded carpet + border) is drawn per-space
            inside SpaceRenderer now, NOT focal-only here — so a space looks the
            same whether it's the focal or a nested preview, and diving/popping
            doesn't make the interior gain a carpet or the outer darken. */}
        <HoverStoreProvider value={hoverStore}>
          <SpaceRenderer
            space={renderFocal}
            scale={captureMode ? 1 : quantZoom}
            viewBounds={captureMode ? undefined : viewBounds}
            depth={0}
            ctx={ctx}
            floorRect={focalFloorRect}
          />
        </HoverStoreProvider>
      </div>
      </ConnectionHoverProvider>
      </ConnectionSelectionProvider>

      {!hideMinimap && (
        <div
          // Mobile: top-right, tucked under the board header (h-11 + gap);
          // bottom corners belong to thumbs and the legend.
          className={
            "absolute pointer-events-auto " + (mobile ? "top-14 right-2" : "bottom-3 right-3")
          }
          data-hud
          data-hud-region="minimap"
          onClick={(e) => e.stopPropagation()}
        >
          <Minimap
            focal={focal}
            selectedId={selectedId}
            highlighted={highlighted}
            // The RO-driven state object — referentially stable across
            // gesture frames (a fresh `screenSize()` measurement would both
            // break the minimap memo and force a layout read per frame).
            screen={canvasSize}
            store={store}
            onCenter={centerOn}
            width={mobile ? 112 : undefined}
            height={mobile ? 78 : undefined}
            className={
              mobile
                ? "cursor-crosshair rounded-xl border border-white/10 bg-slate-900/90 shadow-lg shadow-black/30"
                : undefined
            }
          />
          {/* Zoom readout — corner overlay on the minimap. Lives here
              instead of on the bottom-left ViewControlsHud pill so the
              two HUDs don't fight for real estate. */}
          <div
            className="absolute bottom-1 right-1.5 text-[10px] font-mono text-slate-400 pointer-events-none select-none"
            data-hud-region="zoom"
            title="Current zoom"
          >
            {viewport.zoom.toFixed(2)}×
          </div>
          {/* Dev chip stack, anchored above the minimap: layout debug controls
              (host-fed `minimapExtras`) over the layout-switch toggles. Both
              use the tiny `miniChip` pill so debug reads distinct from the
              proper HUD controls. */}
          {(minimapExtras || import.meta.env.DEV) && (
            <div
              className="absolute bottom-full right-0 mb-1 flex flex-col items-end gap-1"
              data-hud-region="minimap-dev"
            >
              {minimapExtras}
              {import.meta.env.DEV && <DevSwitchesHud />}
            </div>
          )}
          {/* Dev-only viewport readout — pan coords (CSS px) + focal depth. */}
          {import.meta.env.DEV && (
            <div
              className="absolute -bottom-4 right-1.5 whitespace-nowrap text-[9px] font-mono text-slate-500 pointer-events-none select-none"
              data-hud-region="viewport-debug"
              title="Viewport pan (CSS px) · focal depth"
            >
              {pan.x.toFixed(0)},{pan.y.toFixed(0)} · d{viewport.focalPath.length - 1}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function VoidBackdrop() {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at center, rgb(var(--slate-900) / 0.2), rgb(var(--slate-950) / 0.8))",
      }}
    />
  );
}
